import { getCurrentAddress } from '@product-framework/components/FormFields/ValidatedAddress';
import { SnackbarApiErrorMessage } from '@ui-framework/index';

const postProcessedAddressApiData = data => {
  const completeAddress = getCurrentAddress({
    address: data?.address,
    city: data?.city,
    state: data?.state?.name,
    country: data?.country?.countryName,
    zip: data?.postalCode,
    id: data?.id || null,
  });
  return {
    billingAddress: completeAddress,
  };
};

const getMainContact = data => {
  return {
    id: data?.id,
    recordVersion: data?.recordVersion,
    user: {
      id: data?.user?.id,
      firstName: data?.user?.firstName,
      lastName: data?.user?.lastName,
      email: data?.user?.email,
      phoneNumber: data?.user?.phoneNumber,
    },
  };
};

const fetchSP = async params => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}`;
  const response = await params.get(url);
  if (response?.ok) {
    return response.data;
  }
  params.snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={response?.errors} />,
  });
  return null;
};

const fetchContact = async params => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}/contacts?contactType=MAIN&limit=1`;
  const response = await params.get(url);
  if (response?.ok) {
    return response?.data;
  }
  params.snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={response?.errors} />,
  });
  return null;
};

const fetchAddress = async (params, fullResponse = false) => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}/addresses?addressType=MAIN&limit=1`;
  const response = await params.get(url);
  if (response?.ok) {
    if (fullResponse) {
      return response;
    }
    return response.data;
  }
  params.snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={response?.errors} />,
  });
  return null;
};

const fetchDispatchProfile = async params => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-provider-profile/${params.pageId}/dispatch`;
  const response = await params.get(url);
  if (response?.ok) {
    return response.data;
  }
  params.snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={response?.errors} />,
  });
  return null;
};

const editSP = async (params, data, permissions) => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}`;
  const requestData = {
    name: data.name,
    ...(permissions?.accountNumber === 'WRITE' && {
      accountNumber: data.accountNumber,
    }),
    ...(permissions?.status === 'WRITE' && {
      status: data.status,
    }),
    fein: data.fein,
    websiteUrl: data.websiteUrl,
    bmsSystem: data.bmsSystem,
  };
  const response = await params.put(url, requestData);
  return response;
};

const changeSPStatus = async (params, data) => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}`;
  const requestData = {
    status: data.status,
  };
  const response = await params.put(url, requestData);
  return response;
};

const editSPContact = async (params, data) => {
  if (data?.mainContact?.id) {
    const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}/contacts/${
      data?.mainContact?.id
    }`;
    const requestData = {
      userId: data?.mainContact?.user?.id,
      recordVersion: data?.mainContact?.recordVersion,
    };
    const response = await params.put(url, requestData);
    return response;
  }
  const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}/contacts`;
  const requestData = {
    userId: data?.mainContact?.user?.id,
    contactType: 'MAIN',
  };
  const response = await params.post(url, requestData);
  return response;
};

const editSPAddress = async (params, data, formRef) => {
  const defaultValues = formRef.current.getDefaultValues();
  const addressId = defaultValues.fullAddressData?.id;
  let url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}/addresses`;

  let requestData = {};

  if (
    defaultValues?.billingAddress?.address !== data?.billingAddress?.address
  ) {
    requestData = {
      addressType: defaultValues?.fullAddressData?.addressType || 'MAIN',
      placeId: data?.billingAddress?.id,
      placeSessionToken: data?.billingAddressSessionToken,
    };
    let response;
    if (addressId) {
      url = `${url}/${addressId}`;
      response = await params.put(url, requestData);
    } else {
      response = await params.post(url, requestData);
    }
    return response;
  }

  const noChangeResponse = {
    ok: true,
    data: defaultValues?.fullAddressData,
  };

  return noChangeResponse;
};

const addTravelDistance = async (params, data) => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-provider-profile/${params.pageId}/dispatch`;
  const response = await params.put(url, {
    defaultServiceTravelDistance: data?.defaultServiceTravelDistance,
  });
  return response;
};

const getStatusButton = (params, currentStatus, IconLibrary) => {
  const currentStatusIsActive = currentStatus === 'ACTIVE';
  return {
    Icon: currentStatusIsActive ? IconLibrary.SPInActive : IconLibrary.SPActive,
    text: currentStatusIsActive
      ? params?.formatMessage({
          id: 'SET_AS_IN_ACTIVE',
          defaultMessage: 'Set as In-Active',
        })
      : params?.formatMessage({
          id: 'SET_AS_ACTIVE',
          defaultMessage: 'Set as Active',
        }),
  };
};

const resendSPOnboardingEmail = async params => {
  const url = `${params.getApiUrl()}/sxcl/v1/service-providers/${params.pageId}/resend-onboarding-email`;
  const response = await params.post(url);
  return response;
};

export {
  getStatusButton,
  fetchSP,
  fetchContact,
  fetchAddress,
  fetchDispatchProfile,
  editSP,
  editSPAddress,
  addTravelDistance,
  editSPContact,
  postProcessedAddressApiData,
  getMainContact,
  changeSPStatus,
  resendSPOnboardingEmail,
};
